import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Building2, Loader2, Layers } from 'lucide-react';
import { getB2bSuggesties } from '../../services/b2bAcademyApi';
import type { B2bRij, B2bSuggesties } from '../../services/b2bAcademyApi';
import { INKOOP } from './b2bStijl';

const MIN_TEKENS = 3;
const VERTRAGING_MS = 220;

type Item = { soort: 'traject'; id: number } | { soort: 'opleider'; id: number; naam: string } | { soort: 'rij'; rij: B2bRij };

/**
 * Fuzzy probleem- & organisatiezoeker (spec §1): tekstinvoer bovenin de filter-sidebar met een lichte
 * dropdown met live B2B-suggesties (vanaf 3 tekens) — Incompany Trajecten, Erkende Opleiders en
 * de strategische rij waar een organisatieprobleem ("verzuim", "nachtdienst") naar verwijst.
 */
export const B2bZoekbalk: React.FC<{
  waarde: string;
  onChange: (v: string) => void;
  onZoek: (q: string) => void;
  onKiesTraject: (id: number) => void;
  onKiesOpleider: (naam: string) => void;
  onKiesRij: (rij: B2bRij) => void;
}> = ({ waarde, onChange, onZoek, onKiesTraject, onKiesOpleider, onKiesRij }) => {
  const { t } = useTranslation('pages');
  const [open, setOpen] = useState(false);
  const [laden, setLaden] = useState(false);
  const [suggesties, setSuggesties] = useState<B2bSuggesties | null>(null);
  const [actief, setActief] = useState(-1);
  const wrapper = useRef<HTMLDivElement>(null);
  const listId = useId();

  useEffect(() => {
    const q = waarde.trim();
    if (q.length < MIN_TEKENS) {
      setSuggesties(null);
      setLaden(false);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLaden(true);
      try {
        const data = await getB2bSuggesties(q, controller.signal);
        if (!controller.signal.aborted) {
          setSuggesties(data);
          setActief(-1);
        }
      } catch {
        if (!controller.signal.aborted) setSuggesties(null);
      } finally {
        if (!controller.signal.aborted) setLaden(false);
      }
    }, VERTRAGING_MS);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [waarde]);

  useEffect(() => {
    const sluit = (e: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', sluit);
    return () => document.removeEventListener('mousedown', sluit);
  }, []);

  const items: Item[] = suggesties
    ? [
        ...suggesties.rijen.map((r): Item => ({ soort: 'rij', rij: r.key })),
        ...suggesties.trajecten.map((c): Item => ({ soort: 'traject', id: c.id })),
        ...suggesties.opleiders.map((o): Item => ({ soort: 'opleider', id: o.id, naam: o.naam })),
      ]
    : [];
  const heeftSuggesties = items.length > 0;
  const toonDropdown = open && waarde.trim().length >= MIN_TEKENS && (heeftSuggesties || laden);

  const kies = (item: Item) => {
    setOpen(false);
    if (item.soort === 'traject') onKiesTraject(item.id);
    else if (item.soort === 'opleider') onKiesOpleider(item.naam);
    else onKiesRij(item.rij);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (!toonDropdown || actief < 0)) {
      e.preventDefault();
      setOpen(false);
      onZoek(waarde.trim());
      return;
    }
    if (!toonDropdown || !heeftSuggesties) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActief((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActief((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter' && actief >= 0) {
      e.preventDefault();
      kies(items[actief]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  let index = -1;
  const rij = (item: Item, inhoud: React.ReactNode, key: string) => {
    index += 1;
    const i = index;
    return (
      <li key={key} role="option" aria-selected={actief === i} id={`${listId}-${i}`}>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => kies(item)}
          onMouseEnter={() => setActief(i)}
          className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition ${actief === i ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50'}`}
        >
          {inhoud}
        </button>
      </li>
    );
  };
  const kop = (tekst: string) => <li className="px-4 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400" role="presentation">{tekst}</li>;

  const placeholder = t('b2bAcademy.discovery.zoekPlaceholder', { defaultValue: 'Zoek op organisatie, trefwoord of opleider…' });

  return (
    <div ref={wrapper} className="relative w-full min-w-0">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
      <input
        type="search"
        value={waarde}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        aria-label={placeholder}
        role="combobox"
        aria-expanded={toonDropdown}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={actief >= 0 ? `${listId}-${actief}` : undefined}
        autoComplete="off"
        className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40 [&::-webkit-search-cancel-button]:hidden"
      />
      {laden ? (
        <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-500" aria-hidden="true" />
      ) : waarde ? (
        <button type="button" onClick={() => { onChange(''); onZoek(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700" aria-label={t('b2bAcademy.wissen', { defaultValue: 'Wissen' })}>
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}

      {toonDropdown && (
        <ul id={listId} role="listbox" className="absolute left-0 right-0 top-full z-40 mt-1.5 max-h-[26rem] overflow-y-auto rounded-2xl border border-gray-200 bg-white py-1 shadow-xl shadow-black/10 backdrop-blur">
          {laden && !heeftSuggesties && <li className="px-4 py-3 text-sm text-gray-500" role="presentation">{t('b2bAcademy.discovery.suggestiesLaden', { defaultValue: 'Suggesties zoeken…' })}</li>}
          {suggesties && suggesties.rijen.length > 0 && kop(t('b2bAcademy.discovery.suggestieRijen', { defaultValue: 'Organisatieprobleem → strategische rij' }))}
          {suggesties?.rijen.map((r) =>
            rij(
              { soort: 'rij', rij: r.key },
              <>
                <span className="text-lg" aria-hidden="true">{r.emoji}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{r.label}</span>
                  <span className="block truncate text-xs text-gray-500">{t('b2bAcademy.discovery.viaTerm', { defaultValue: 'via "{{term}}"', term: r.term })}</span>
                </span>
                <Layers className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </>,
              `rij-${r.key}`
            )
          )}
          {suggesties && suggesties.trajecten.length > 0 && kop(t('b2bAcademy.discovery.suggestieTrajecten', { defaultValue: 'Incompany trajecten' }))}
          {suggesties?.trajecten.map((c) =>
            rij(
              { soort: 'traject', id: c.id },
              <>
                <span className="text-lg" aria-hidden="true">{c.rij_emoji}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{c.titel}</span>
                  <span className="block truncate text-xs text-gray-500">{c.organisatie_naam} · {INKOOP[c.inkoop_type].emoji} {t(`b2bAcademy.inkoop.${c.inkoop_type}`, { defaultValue: INKOOP[c.inkoop_type].label })}</span>
                </span>
              </>,
              `traject-${c.id}`
            )
          )}
          {suggesties && suggesties.opleiders.length > 0 && kop(t('b2bAcademy.discovery.suggestieOpleiders', { defaultValue: 'Erkende opleiders' }))}
          {suggesties?.opleiders.map((o) =>
            rij(
              { soort: 'opleider', id: o.id, naam: o.naam },
              <>
                {o.logo_url ? <img src={o.logo_url} alt="" className="h-7 w-7 rounded-md bg-white object-contain p-0.5" /> : <Building2 className="h-5 w-5 text-gray-500" aria-hidden="true" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{o.naam}</span>
                  <span className="block text-xs text-gray-500">{t('b2bAcademy.discovery.aantalTrajecten', { defaultValue: '{{n}} incompany trajecten', n: o.aantal })}</span>
                </span>
              </>,
              `opleider-${o.id}`
            )
          )}
        </ul>
      )}
    </div>
  );
};

export default B2bZoekbalk;
